import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Button, FullscreenShell, Panel, StatGrid, VolumeControl } from "@legends/ui";
import { Room, RoomEvent } from "livekit-client";
import { ChatRail, useLiveChat } from "./liveChat";
import WhiteboardCanvas, { whiteboardLayout } from "./Whiteboard";
import { AdSurface } from "./AdSurface";
import { RollPlayer } from "./RollPlayer";
import {
  ApiError,
  fetchResolvedRoll,
  track,
  watchBroadcast,
  renderJoin,
  fetchWatchProgram,
  submitPlaybackReport,
  type Legend,
  type ProgramState,
  type RollItem,
  type TransitionType,
  type WatchSource,
} from "./api";
import { createDiagnostics } from "./playbackDiagnostics";
import { ConnectionBanner, useConnState, type SourceKind } from "./liveConnection";
import { useHlsPlayback } from "./hlsPlayback";
import { resolveBoardImageUrl, useBoardSlates } from "./boardSlates";
import { useDirectorOnAirAudio } from "./directorMic";
import { getSharedAudioContext, setAudioSessionType, unlockAudio } from "./audioUnlock";
import { IS_IOS, loadWatchVolume, loadWatchMuted, saveWatchVolume, saveWatchMuted } from "./watchAudioPrefs";
import { isMultiHead, layoutContainerStyle, layoutTileStyle, layoutSlots, layoutHasBoard, layoutHasSharedBoard } from "./programLayout";
import { attachProgramSlot, type AttachableTrack } from "./programAttach";
import { snapshotKeyframes, snapshotTiming, transitionForSwap } from "./programTransition";
import { alertStyle, monoLabelStyle } from "./sections";

// Consumer watch surface (ADR 0012 — Phase 3). A ticket holder watches the live
// program of a cave event. The program is shared state composited client-side:
// the initial state arrives with the watch response, live updates arrive as
// LiveKit data messages. The viewer subscribes to the participant whose identity
// === program.source and draws the active overlay on top. Dev delivery is
// small-room WebRTC (subscribe-only viewer token); the HLS seam is plumbed for
// prod but never exercised in dev.

type Phase = "loading" | "webrtc" | "hls" | "ticket-required" | "error";

// Grace before the involuntary "Signal lost" slate (ADR 0042) actually shows
// (ADR 0062). A deliberate End/standby relays its terminal state around the same
// moment the on-program source drops during teardown; waiting a beat lets that
// intent win so a fan never sees "Signal lost" flash for a voluntary end. A
// genuine drop still surfaces the slate right after the short grace. Kept well
// under the 1s test-timeout so the source-loss slate still appears in time.
const SIGNAL_LOST_GRACE_MS = 600;
// Program-state backstop poll (ADR 0091): interval, and the quiet-window a data
// message must be older than before a poll re-syncs (so the poll never reverts a
// just-received update that is still persisting).
const PROGRAM_POLL_MS = 7000;
const PROGRAM_POLL_GRACE_MS = 4000;
// Pre-live go-live poll (LSC-574): a fan who joined BEFORE the Director's TAKE must
// transition to live even if the TAKE's data-channel message is dropped under load.
// The general backstop above is grace-gated (it skips within 4s of any data message,
// so a Director relaying through the pre-live warmup can keep it skipping); this one
// is dedicated, fast, and UNGATED — safe because latching "live" is monotonic (it only
// resets on a fresh go-live), so it can never revert a mid-show update. It self-stops
// the moment the show is live.
const GOLIVE_POLL_MS = 2500;

// The fan's own listening volume + mute (ADR 0064), persisted for the session so
// it survives a layout swap / full-screen toggle / re-attach. This is the fan's
// OWN volume — distinct from the autoplay-policy "Enable audio" gesture (ADR
// 0035) and the production/hold mute (ADR 0053/0055). Reads/writes are wrapped
// so a locked-down or unavailable sessionStorage never crashes the surface.
// Theater mode (LSC-505): a fan-chosen viewing mode that collapses the interaction
// deck (Ask / sponsors / stats / whiteboard panel, or the full-screen Ask rail) so
// the 16:9 Program uses substantially more of the screen. Session-persisted like the
// volume pref so it survives a layout swap / full-screen toggle; a small restore
// control always brings the deck back. Default off (interaction-first stays default).
const THEATER_KEY = "legends.watch.theater";

// Fan volume/mute + iOS detection live in ./watchAudioPrefs (shared with the
// on-demand replay player so the custom control behaves identically, LSC-544).
function loadTheater(): boolean {
  try {
    return sessionStorage.getItem(THEATER_KEY) === "1";
  } catch {
    return false;
  }
}

const surfaceStyle: CSSProperties = {
  margin: "8px 0 4px",
  padding: "14px 16px 16px",
  background: "var(--well)",
  border: "1px solid var(--border)",
  display: "grid",
  gap: 12,
};

const stageStyle: CSSProperties = {
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 9",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  overflow: "hidden",
};

const videoStyle: CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

// The "Report a problem" note/menu card (LSC-598) — a small translucent panel over the
// stage bottom-left; never covers the whole frame.
const reportNoteStyle: CSSProperties = {
  background: "rgba(12,12,15,0.86)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  padding: "8px 10px",
  fontSize: "var(--fs-xs)",
  lineHeight: 1.5,
  color: "var(--text)",
  boxShadow: "var(--shadow-pop)",
};

const standbyStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  textAlign: "center",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-2xs)",
  letterSpacing: "var(--ls-label)",
  color: "var(--text-dim)",
};

// A per-tile signal-lost slate in a multi-head layout (ADR 0054) — one head
// dropped while the other keeps playing.
const tileLostStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 12,
  textAlign: "center",
  background: "radial-gradient(120% 120% at 50% 0%, #241f1a 0%, #0c0c0f 70%)",
  color: "var(--text-hi)",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-2xs)",
  letterSpacing: "var(--ls-label)",
};

const lowerThirdStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  padding: "8px 12px",
  background: "var(--scrim, rgba(0,0,0,0.62))",
  color: "var(--text-hi)",
  fontSize: "var(--fs-xs)",
};

// The intentional audio-led stage (ADR 0043) — a designed base layer that
// replaces the blank/black feed when the on-program source publishes mic only.
const audioStageStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  padding: 16,
  textAlign: "center",
  background: "radial-gradient(120% 120% at 50% 0%, #23242a 0%, #0c0c0f 70%)",
};

// The intentional pre-start slate (ADR 0064) — the designed pre-live/waiting
// experience (a distinct slate, not "the Cave"), shown before the broadcast goes
// live when no pre-roll playlist (ADR 0052) is configured. Reuses the legend/cave
// context (name/initials) like the audio-led stage. Distinct from standby (a
// mid-show pause) and ended (terminal).
const preStartStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  padding: 16,
  textAlign: "center",
  background: "radial-gradient(120% 120% at 50% 0%, #23242a 0%, #0c0c0f 70%)",
};

// A compact "memorabilia" initials mat — the audio-led hero when the legend is
// known (mirrors the Legend Cave headshot treatment, ADR 0020).
const audioInitialsStyle: CSSProperties = {
  width: 88,
  height: 88,
  borderRadius: "50%",
  border: "1px solid var(--border-mute)",
  background: "linear-gradient(160deg, var(--stone-600), var(--stone-800))",
  boxShadow: "inset 0 0 0 4px rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "var(--font-display)",
  fontSize: 36,
  fontWeight: 600,
  color: "var(--gold)",
  letterSpacing: "0.04em",
};

export interface ConsumerWatchProps {
  broadcastId: string;
  onClose?: () => void;
  /** Invoked by "Get a ticket" when watch is ticket-gated — the storefront uses
   *  it to close the panel and jump to the tickets. Falls back to onClose. */
  onGetTicket?: () => void;
  /** The headlining legend — enriches the watch into the Fan View (ADR 0020):
   *  athlete live-stats + a sponsor rail alongside the fan chat. */
  legend?: Legend | null;
  /** Headless program-renderer mode (LSC-429): when set, bootstrap via the render
   *  code (subscribe-only, no session) instead of the fan watch, and render ONLY the
   *  program stage — no fan chrome. This is what the LiveKit composite egress loads
   *  so HLS/VOD show the produced program. Fan analytics / chat / the ticket gate are
   *  all skipped. */
  renderCode?: string;
}

/** Watch the live program of a broadcast (ticket-gated), or — with renderCode — the
 *  headless program renderer for egress capture (LSC-429). */
export default function ConsumerWatch({ broadcastId, onClose, onGetTicket, legend, renderCode }: ConsumerWatchProps) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [gate, setGate] = useState<"ticket" | "signin" | "unavailable">("ticket");
  // The server's own 403 message, shown verbatim for the "unavailable" gate (LSC-620)
  // so a holder who hits a private rehearsal isn't falsely told to buy a ticket.
  const [gateMsg, setGateMsg] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [program, setProgram] = useState<ProgramState>({});
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  // The fan's own listening volume + mute (ADR 0064), session-persisted. Applied
  // to the program media element(s) below; distinct from the autoplay-policy
  // "Enable audio" gesture and the production/hold mute (ADR 0053/0055).
  const [volume, setVolume] = useState<number>(loadWatchVolume);
  const [userMuted, setUserMuted] = useState<boolean>(loadWatchMuted);
  const setVolumePersist = (v: number) => {
    setVolume(v);
    saveWatchVolume(v);
  };
  const setMutedPersist = (m: boolean) => {
    setUserMuted(m);
    saveWatchMuted(m);
  };
  // Theater mode (LSC-505): collapse the interaction deck to maximize Program.
  const [theater, setTheater] = useState<boolean>(loadTheater);
  const setTheaterPersist = (t: boolean) => {
    setTheater(t);
    try {
      sessionStorage.setItem(THEATER_KEY, t ? "1" : "0");
    } catch {
      // sessionStorage unavailable — keep the in-memory value
    }
  };
  // Whether the program source currently resolves to a live participant/track.
  // False while set-but-absent = involuntary source loss (ADR 0042).
  const [sourceLive, setSourceLive] = useState(false);
  // Whether the program has EVER been live this cycle (ADR 0101). The pre-start
  // "Starting soon" slate must PERSIST until the first live frame — not vanish the
  // instant a source is merely assigned (the Beta Test 8 "brief flash then black").
  // So pre-start is gated on "never been live", and the involuntary signal-lost
  // slate only applies AFTER the show has been live (a genuine mid-show drop).
  // Reset on end so a fresh go-live shows the waiting slate again.
  const [hasBeenLive, setHasBeenLive] = useState(false);
  // Whether the single-layout "Signal lost" slate is actually shown. Driven by a
  // grace timer (ADR 0062) so a source drop during a deliberate End/standby
  // teardown never flashes it; a genuine loss surfaces it after the short grace.
  const [showSourceLost, setShowSourceLost] = useState(false);
  // The live media kind of the on-program source (ADR 0043): "video" | "audio"
  // (mic-led CaveCast) | "none". Drives the intentional audio-led stage.
  const [sourceKind, setSourceKind] = useState<SourceKind>("none");
  // Slot B's live media kind in a multi-head layout (ADR 0054); "none" whenever the
  // layout is single. Drives slot B's per-tile signal-lost slate.
  const [sourceKindB, setSourceKindB] = useState<SourceKind>("none");
  // Slots C/D's live media kind (ADR 0212 — 3/4-up layouts); "none" unless the layout
  // uses that slot. Each drives its own per-tile signal-lost slate.
  const [sourceKindC, setSourceKindC] = useState<SourceKind>("none");
  const [sourceKindD, setSourceKindD] = useState<SourceKind>("none");
  const [conn, setConn] = useConnState();
  const [reloadKey, setReloadKey] = useState(0); // bumped by Rejoin to re-run connect
  // The cave's resolved pre/post-roll looping playlists (ADR 0052): the pre-roll
  // loops in the pre-live/waiting state, the post-roll in the ended state,
  // replacing the static slates when non-empty. caveId arrives with the watch
  // response (falls back to the headlining legend's cave).
  const [caveId, setCaveId] = useState<string>(legend?.caveId ?? "");
  const [rollPre, setRollPre] = useState<RollItem[]>([]);
  const [rollPost, setRollPost] = useState<RollItem[]>([]);
  // The cave's resolved rotating HOLD playlist (ADR 0055 — replaced ADR 0053's
  // single hold image): looped over the stage when the director/legend PAUSES
  // program video. Empty → a text "Paused" slate fallback.
  const [rollHold, setRollHold] = useState<RollItem[]>([]);
  // Timed auto-resume tick (ADR 0055): a bump forces the hold-active recompute
  // when program.resumeAt elapses, so the visual hold clears with no further
  // action even before the server reaper runs.
  const [holdTick, setHoldTick] = useState(0);
  const [room, setRoom] = useState<Room | null>(null); // re-render on connect; chat is API-driven now
  // Chat rides the API now (ADR 0026), so it works viewer→publisher and on HLS.
  // The headless renderer (LSC-429) has no chat UI and no session — never poll chat.
  const chat = useLiveChat(broadcastId, !renderCode && (phase === "webrtc" || phase === "hls"));
  // The board-slate catalog (ADR 0058): an on-program board may be an admin-uploaded
  // image slate, resolved to its background image and drawn read-only for the Fan.
  const boardSlates = useBoardSlates();
  // Director mic → On Air (ADR 0247, LSC-633): attach the Director's PUBLIC on-air mic
  // (track `director-onair`) so a fan hears the Director when they're On Air — a separate
  // audio plane from the program source, so it's independent of the selected layout/slate
  // and honours only the fan's OWN volume/mute (not the program-source slate muting). The
  // headless program-renderer shares this component, so the recording/replay captures it
  // too, by construction. Off-air = the track is muted (silence); the SFU permission (set
  // by the Director console) is the hard gate — a private talkback cue never reaches here.
  const { containerRef: directorMicRef } = useDirectorOnAirAudio(room, { volume, muted: userMuted });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // HLS live delivery (ADR 0140): when the watch grant is mode:"hls", play the
  // segmented playlist into the program <video> (native on Safari, hls.js elsewhere)
  // — that's what makes AirPlay / PiP / Cast work. No-op in the WebRTC path.
  useHlsPlayback(videoRef, hlsUrl, phase === "hls", undefined, true);
  // Slot B's video/audio elements + attached tracks (multi-head layouts, ADR 0054).
  const videoRefB = useRef<HTMLVideoElement | null>(null);
  const audioRefB = useRef<HTMLAudioElement | null>(null);
  // Slots C/D (ADR 0212 — 3/4-up + board-dominant layouts): each active head gets its
  // own <video>/<audio> element so all participants in the composition stay audible.
  const videoRefC = useRef<HTMLVideoElement | null>(null);
  const audioRefC = useRef<HTMLAudioElement | null>(null);
  const videoRefD = useRef<HTMLVideoElement | null>(null);
  const audioRefD = useRef<HTMLAudioElement | null>(null);
  const roomRef = useRef<Room | null>(null);
  const programRef = useRef<ProgramState>({});
  const attachedVideoRef = useRef<AttachableTrack | null>(null);
  const attachedAudioRef = useRef<AttachableTrack | null>(null);
  const attachedVideoRefB = useRef<AttachableTrack | null>(null);
  const attachedAudioRefB = useRef<AttachableTrack | null>(null);
  const attachedVideoRefC = useRef<AttachableTrack | null>(null);
  const attachedAudioRefC = useRef<AttachableTrack | null>(null);
  const attachedVideoRefD = useRef<AttachableTrack | null>(null);
  const attachedAudioRefD = useRef<AttachableTrack | null>(null);
  const mountedRef = useRef(true);
  const joinedRef = useRef(false); // fired live_joined → owe a live_left
  const lostReportedRef = useRef(false); // one playback_failed per unresolved streak
  // Playback diagnostics recorder (LSC-598): a bounded ring buffer of this watch session's
  // diagnostic signals, fed at the existing event points below. Skipped for the headless
  // program renderer (no fan, no report). diagAudioBlockedRef de-dupes the audio-state taps.
  const diag = useMemo(() => (renderCode ? null : createDiagnostics(broadcastId)), [renderCode, broadcastId]);
  const diagAudioBlockedRef = useRef(false);
  const [report, setReport] = useState<{ state: "idle" | "sending" | "done"; ref?: string }>({ state: "idle" });
  const [reportOpen, setReportOpen] = useState(false);
  // Whether an active program slot is CURRENTLY signal-lost (source set but resolved to
  // no live track, ADR 0042). Set by attachSource; read by applyProgram so a same-value
  // re-take or the backstop poll force-re-attaches a genuinely-dropped source (LSC-491)
  // while a healthy update stays non-force (no flicker, ADR 0062).
  const lostRef = useRef(false);
  // When we last applied a program update from the LiveKit data channel — the
  // backstop poll (ADR 0091) skips while data is flowing (the fan is current) and
  // only re-syncs once the channel goes quiet, so it can never revert a just-
  // received update that hasn't finished persisting.
  const lastProgramMsgRef = useRef(0);

  // Program-swap transition (ADR 0081/0082). When a TAKE carries a non-cut
  // transition and the composition actually changes, we freeze the OUTGOING frame
  // into this canvas and animate it away to reveal the freshly-attached new
  // program underneath — the attach path (ADR 0042/0062) is untouched. Best-effort
  // + animation-only: if the frame can't be captured we just cut.
  const snapshotCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Minimal state: a running transition's id + its type/ms. The WAAPI animation is
  // driven imperatively by the effect below; React only tracks presence (to toggle
  // the snapshot's visibility + guard against slates) — no phase machine (LSC-176).
  const [snap, setSnap] = useState<{ id: number; type: TransitionType; ms: number } | null>(null);
  const snapTimersRef = useRef<number[]>([]);
  const snapAnimRef = useRef<Animation | null>(null);
  const snapIdRef = useRef(0);

  // Resolve + attach one program source (matched by STABLE key, ADR 0042), returning the
  // live media kind. Delegates to the shared attachProgramSlot (programAttach.ts) so the
  // Fan watch and the talent program monitors (ProgramComposition, LSC-561) resolve a slot
  // to the SAME source (LSC-177). The source-resolution / ghost-skip / ensureSubscribe /
  // keep-on-miss logic lives there; this just binds roomRef.
  const attachOne = (
    sourceKey: string | undefined,
    vid: HTMLVideoElement | null,
    audio: HTMLAudioElement | null,
    videoRefHolder: { current: AttachableTrack | null },
    audioRefHolder: { current: AttachableTrack | null },
    force: boolean,
  ): SourceKind => attachProgramSlot(roomRef.current, sourceKey, vid, audio, videoRefHolder, audioRefHolder, force);

  // Follow the program source(s) for both video and audio. In a multi-head layout
  // (ADR 0054) BOTH heads' audio is attached and played — two people talking — and
  // each tile classifies its own source for a per-tile signal-lost slate. `force`
  // re-attaches even unchanged tracks (element remount / hold lift, ADR 0062);
  // otherwise the attach is idempotent so an unrelated join/leave doesn't flicker.
  const attachSource = (force = false) => {
    const prog = programRef.current;
    // Which head slots this layout composes (ADR 0054/0212). A slot NOT in the layout
    // is attached with an undefined source + null element, so switching back to a
    // smaller layout detaches the extra publishers' tracks (never leaks their audio).
    const slots = layoutSlots(prog.layout);
    const useB = slots.includes("b");
    const useC = slots.includes("c");
    const useD = slots.includes("d");
    const kindA = attachOne(prog.source, videoRef.current, audioRef.current, attachedVideoRef, attachedAudioRef, force);
    const kindB = attachOne(
      useB ? prog.sourceB : undefined,
      useB ? videoRefB.current : null,
      useB ? audioRefB.current : null,
      attachedVideoRefB,
      attachedAudioRefB,
      force,
    );
    const kindC = attachOne(
      useC ? prog.sourceC : undefined,
      useC ? videoRefC.current : null,
      useC ? audioRefC.current : null,
      attachedVideoRefC,
      attachedAudioRefC,
      force,
    );
    const kindD = attachOne(
      useD ? prog.sourceD : undefined,
      useD ? videoRefD.current : null,
      useD ? audioRefD.current : null,
      attachedVideoRefD,
      attachedAudioRefD,
      force,
    );
    // A slot is "currently lost" when its source is SET but resolved to no live track
    // (ADR 0042). Record it so applyProgram can force a re-attach on a same-value
    // re-take / backstop re-sync of a genuinely-dropped source (LSC-491).
    lostRef.current =
      (!!prog.source && kindA === "none") ||
      (useB && !!prog.sourceB && kindB === "none") ||
      (useC && !!prog.sourceC && kindC === "none") ||
      (useD && !!prog.sourceD && kindD === "none");
    if (mountedRef.current) {
      setSourceLive(kindA !== "none");
      setSourceKind(kindA);
      setSourceKindB(kindB);
      setSourceKindC(kindC);
      setSourceKindD(kindD);
    }
  };

  const clearSnapTimers = () => {
    snapTimersRef.current.forEach((t) => window.clearTimeout(t));
    snapTimersRef.current = [];
    if (snapAnimRef.current) {
      try {
        snapAnimRef.current.cancel();
      } catch {
        /* ignore */
      }
      snapAnimRef.current = null;
    }
  };

  // Freeze the CURRENT (outgoing) program frame into the snapshot canvas and arm
  // the reveal animation. Called BEFORE programRef/attachSource swap in the source,
  // so the canvas captures the old picture; the new track then attaches underneath
  // and shows through as the snapshot animates away. Best-effort — if there's no
  // usable frame (canvas/video not ready, empty video) we bail and it's a plain cut.
  const startTransition = (type: TransitionType, ms: number) => {
    const canvas = snapshotCanvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    } catch {
      return; // tainted/unavailable — just cut
    }
    clearSnapTimers();
    const id = ++snapIdRef.current;
    // Mark the snapshot present (visible over the new program). The WAAPI animation
    // itself is started imperatively by the effect below AFTER this commit makes the
    // canvas visibility:visible, so it never runs on a hidden element.
    setSnap({ id, type, ms });
  };

  // Run the outgoing-snapshot animation with the Web Animations API (LSC-176). This
  // fires AFTER render (the canvas is visibility:visible), and — unlike a CSS
  // transition whose transition-property + value changed in one React commit
  // (ADR 0098/0144, which still "jumped" on WebKit/iOS) — WAAPI is imperative and
  // compositor-driven, so the crossfade/wipe/slide actually plays. jsdom / very old
  // engines lack element.animate; there we fall back to a plain timed teardown (an
  // instant cut) so nothing throws and the show is never blocked.
  useEffect(() => {
    if (!snap) return;
    const id = snap.id;
    const canvas = snapshotCanvasRef.current;
    const done = () => {
      if (mountedRef.current && snapIdRef.current === id) setSnap(null);
    };
    if (canvas && typeof canvas.animate === "function") {
      let anim: Animation | null = null;
      try {
        anim = canvas.animate(snapshotKeyframes(snap.type), snapshotTiming(snap.type, snap.ms));
      } catch {
        anim = null;
      }
      if (anim) {
        snapAnimRef.current = anim;
        anim.onfinish = done;
        anim.oncancel = () => {
          /* superseded/torn down — clearSnapTimers owns the state */
        };
        // Backstop teardown in case onfinish never fires (tab backgrounded mid-run).
        snapTimersRef.current.push(window.setTimeout(done, snap.ms + 200));
        return () => {
          if (snapAnimRef.current === anim) snapAnimRef.current = null;
        };
      }
    }
    // No WAAPI — instant cut: tear the frozen frame down promptly.
    snapTimersRef.current.push(window.setTimeout(done, snap.ms + 60));
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap?.id]);

  const applyProgram = (state: ProgramState) => {
    const prev = programRef.current;
    // A non-cut TAKE that actually swaps the composition animates client-side
    // (ADR 0082) — freeze the outgoing frame first, then swap + re-attach as usual.
    const t = transitionForSwap(prev, state);
    if (t) startTransition(t.type, t.ms);
    programRef.current = state;
    if (mountedRef.current) setProgram(state);
    // Normally idempotent (ADR 0062 no-flicker): only the source/layout-change effect
    // forces a re-attach, so an overlay/hold-only update never churns the live feed.
    // EXCEPTION (LSC-491): if a slot is CURRENTLY signal-lost, force — so a Director
    // re-take of the SAME source (unchanged value → the value-change effect won't fire)
    // and the backstop poll deterministically re-run ensureSubscribed + re-attach,
    // instead of a no-op that strands the fan until the Director switches sources + back.
    attachSource(lostRef.current);
  };

  // iOS 17+ audio session (LSC-638): while a fan Watch is mounted, treat its Web Audio
  // mix as media PLAYBACK (not "ambient", which the ring/silent switch mutes); restore
  // "auto" on leave so the rest of the app never interrupts a fan's background audio.
  useEffect(() => {
    if (renderCode) return;
    setAudioSessionType("playback");
    return () => setAudioSessionType("auto");
  }, [renderCode]);

  useEffect(() => {
    mountedRef.current = true;

    const connect = async (src: WatchSource) => {
      // webAudioMix mixes ALL remote audio into ONE Web Audio sink instead of one <audio>
      // element per track (LSC-598). iOS Safari can't sustain two simultaneous media
      // elements — on a 2-up TAKE the 2nd head's element suspended the single iOS audio
      // session and ALL Program audio died — so N-source compositions need the single-sink
      // path. It also makes the fan volume slider actually work on iOS (element.volume is
      // inert there, ADR 0076). Gated OFF for the headless program-render egress
      // (`renderCode`): it runs in headless Chrome where multi-element is fine, and we don't
      // touch the egress audio-capture routing under this fix.
      //
      // LSC-638: hand the Room the APP-owned AudioContext (audioUnlock.ts) rather than
      // letting LiveKit create one at connect time. On iOS a context only runs when
      // created/resumed inside a user gesture; LiveKit's is born after the async token
      // fetch → `suspended` → a silent Watch until some later tap (the "enter fullscreen
      // to get audio" symptom). The shared context was already resumed by the fan's
      // earlier taps (home → Cave → Watch), and on a cold load any tap resumes it. A
      // provided context is never closed by LiveKit on disconnect, so it survives
      // leaving/re-entering Watch.
      const sharedCtx = renderCode ? undefined : getSharedAudioContext();
      const room = new Room(
        renderCode ? undefined : { webAudioMix: sharedCtx ? { audioContext: sharedCtx } : true },
      );
      roomRef.current = room;

      room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
        try {
          const msg = JSON.parse(new TextDecoder().decode(payload)) as {
            type?: string;
            state?: ProgramState;
          };
          if (msg.type === "program" && msg.state) {
            applyProgram(msg.state);
            lastProgramMsgRef.current = Date.now();
          }
        } catch {
          // ignore malformed / unrelated data messages
        }
      });
      room.on(RoomEvent.TrackSubscribed, () => attachSource());
      room.on(RoomEvent.TrackUnsubscribed, () => attachSource());
      // A second/late publisher publishing (ADR 0075): attachSource resolves the
      // program source and force-subscribes it if it's on program but unsubscribed,
      // so the tile recovers without waiting for autoSubscribe or a page refresh.
      room.on(RoomEvent.TrackPublished, () => attachSource());
      // Under many viewers the SFU can outright FAIL a (late/second) subscribe —
      // LiveKit fires TrackSubscriptionFailed and nothing else would retry it, so
      // the tile would sit on a permanent signal-loss slate that only a refresh
      // clears (ADR 0075). Re-request immediately (ensureSubscribed re-emits the
      // subscription), and the lengthened retry backoff below backstops a slow SFU.
      room.on(RoomEvent.TrackSubscriptionFailed, () => {
        diag?.record("track:subfailed"); // the SFU outright failed a subscribe (ADR 0075)
        attachSource();
      });
      room.on(RoomEvent.ParticipantConnected, () => attachSource());
      room.on(RoomEvent.ParticipantDisconnected, () => attachSource());
      // Audio-blocked = the Enable-audio control. With the shared context, ITS state is
      // authoritative: audio flows the moment it's `running`, however it got resumed (our
      // first-gesture listener, LiveKit's, or Enable audio) — LiveKit's canPlaybackAudio
      // only updates on its own startAudio path, so it can stay false (stale button) or,
      // worse, never report a still-suspended context. iOS's non-standard "interrupted"
      // (phone call) also reads as blocked → a tap brings it back.
      const syncBlocked = () => {
        if (!mountedRef.current) return;
        const blocked = sharedCtx ? sharedCtx.state !== "running" : !room.canPlaybackAudio;
        setAudioBlocked(blocked);
        // Record only the transition (LSC-598): intended/engine-blocked audio vs resumed —
        // distinguishes "the fan muted" from "the browser suspended/interrupted playback".
        if (blocked !== diagAudioBlockedRef.current) {
          diagAudioBlockedRef.current = blocked;
          diag?.record(blocked ? "audio:blocked" : "audio:resumed", sharedCtx ? sharedCtx.state : undefined);
        }
      };
      room.on(RoomEvent.AudioPlaybackStatusChanged, syncBlocked);
      if (sharedCtx) {
        sharedCtx.addEventListener("statechange", syncBlocked);
        room.once(RoomEvent.Disconnected, () => sharedCtx.removeEventListener("statechange", syncBlocked));
      }
      // Reconnect messaging (ADR 0042) — surface the transport state.
      room.on(RoomEvent.Reconnecting, () => {
        diag?.record("conn:reconnecting");
        if (mountedRef.current) setConn("reconnecting");
      });
      room.on(RoomEvent.Reconnected, () => {
        diag?.record("conn:reconnected"); // recovery succeeded (AC 5)
        if (mountedRef.current) {
          setConn("reconnected");
          attachSource(true); // reconnect may re-mint tracks — force a re-attach
        }
      });
      room.on(RoomEvent.Disconnected, () => {
        diag?.record("conn:lost");
        // A hard drop (not our own teardown): offer a rejoin.
        if (mountedRef.current && roomRef.current === room) setConn("lost");
      });

      await room.connect(src.url ?? "", src.token ?? "");
      if (!mountedRef.current) {
        void room.disconnect();
        return;
      }
      setRoom(room); // enables the live chat channel
      syncBlocked(); // a cold load (no gesture yet) surfaces Enable audio right away
      // The initial program came with the watch response; the source may already
      // be present in the room, so try to attach right away.
      attachSource();
    };

    // The headless renderer (LSC-429) bootstraps via its render code (subscribe-only,
    // no session) and emits no fan analytics; a fan watch tracks + uses the watch gate.
    if (!renderCode) track("cavecast_viewed", { cavecastId: broadcastId });

    (renderCode ? renderJoin(renderCode) : watchBroadcast(broadcastId))
      .then(async (src) => {
        if (!mountedRef.current) return;
        if (src.caveId) setCaveId(src.caveId);
        applyProgram(src.program ?? {});
        joinedRef.current = true;
        diag?.record("watch:joined", src.mode); // delivery mode (webrtc/hls) at join
        if (!renderCode) track("live_joined", { cavecastId: broadcastId, props: { mode: src.mode } });
        if (src.mode === "hls") {
          setHlsUrl(src.url ?? null);
          setPhase("hls");
          return;
        }
        setPhase("webrtc");
        await connect(src);
        // Egress custom-template contract (LSC-430/431): once the room is connected +
        // the program applied, tell the LiveKit recorder to begin capturing. The
        // contract is a plain console signal (`@livekit/egress-sdk` is only a wrapper);
        // harmless in a normal browser (nothing reads it) — only the egress does.
        if (renderCode && mountedRef.current) console.log("START_RECORDING");
      })
      .catch((err: unknown) => {
        if (!mountedRef.current) return;
        if (err instanceof ApiError && err.status === 403) {
          // The watch gate returns distinct 403s: an entitlement denial names a ticket;
          // a private-rehearsal denial (ADR 0244) is a neutral "not available." Don't
          // tell a fan who may already hold a ticket to buy one (LSC-620) — surface the
          // server's own message and drop the storefront CTA unless it's a ticket issue.
          const m = err.message || "";
          const generic = /^API returned \d+$/.test(m);
          if (!m || generic || /ticket/i.test(m)) {
            setGate("ticket");
          } else {
            setGate("unavailable");
            setGateMsg(m);
          }
          setPhase("ticket-required");
          return;
        }
        if (err instanceof ApiError && err.status === 401) {
          // Not authenticated on this cave host — a clear "sign in" gate beats
          // the old "Standby … / API returned 401" dead-end (ADR 0018).
          setGate("signin");
          setPhase("ticket-required");
          return;
        }
        setError(errMsg(err));
        setPhase("error");
        diag?.record("watch:error", errMsg(err));
        track("playback_failed", { cavecastId: broadcastId, props: { error: errMsg(err) } });
      });

    return () => {
      mountedRef.current = false;
      // Egress custom-template contract (LSC-430/431): signal the recorder to finalize
      // on teardown (the server StopEgress on broadcast end is the primary stop; this is
      // the clean template-side finalize). No-op in a normal browser.
      if (renderCode) console.log("END_RECORDING");
      clearSnapTimers();
      if (joinedRef.current) {
        joinedRef.current = false;
        diag?.record("watch:left");
        if (!renderCode) track("live_left", { cavecastId: broadcastId });
      }
      const room = roomRef.current;
      roomRef.current = null;
      const attachedVideo = attachedVideoRef.current;
      attachedVideoRef.current = null;
      if (attachedVideo) {
        try {
          attachedVideo.detach();
        } catch {
          // ignore
        }
      }
      const attachedAudio = attachedAudioRef.current;
      attachedAudioRef.current = null;
      if (attachedAudio) {
        try {
          attachedAudio.detach();
        } catch {
          // ignore
        }
      }
      // Slot B tracks (multi-head, ADR 0054).
      const attachedVideoB = attachedVideoRefB.current;
      attachedVideoRefB.current = null;
      if (attachedVideoB) {
        try {
          attachedVideoB.detach();
        } catch {
          // ignore
        }
      }
      const attachedAudioB = attachedAudioRefB.current;
      attachedAudioRefB.current = null;
      if (attachedAudioB) {
        try {
          attachedAudioB.detach();
        } catch {
          // ignore
        }
      }
      if (room) void room.disconnect();
      setRoom(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcastId, reloadKey]);

  // Rejoin after a hard disconnect — re-run the connect effect from a clean slate.
  const rejoin = () => {
    setConn("live");
    setReloadKey((k) => k + 1);
  };

  // Toggling full-screen remounts the <video>; re-point the live track at it
  // (force — the element changed even though the track didn't).
  useEffect(() => {
    attachSource(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen]);

  // Switching between single and a multi-head layout (ADR 0054) — or changing a
  // slot's assigned source — remounts the slot <video>/<audio> elements, so
  // re-point the tracks at the fresh refs once React has committed the new
  // composition. Keyed on the composition shape only (not every render) so we
  // don't re-attach tracks needlessly (force — the elements just remounted).
  useEffect(() => {
    attachSource(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.layout, program.source, program.sourceB, program.sourceC, program.sourceD, phase]);

  // Bounded recovery retries (ADR 0075): when a source is on program but resolves
  // to nothing while we're live (not a voluntary standby/end), re-attempt attach a
  // few times over a few seconds. This backstops a missed/late TrackPublished or
  // TrackSubscribed event and gives a force-requested subscription time to land —
  // turning a stuck "Signal lost" (the second-publisher race that a refresh only
  // half-fixed) into a transient one. Re-runs whenever the resolved kinds change,
  // so it stops as soon as the source recovers.
  // Program-state backstop poll (ADR 0091). Program updates (a lower-third taken
  // live, a source switch, a hold) reach viewers over the LiveKit data channel,
  // which some viewers miss under load — with no re-sync they never converge (the
  // "lower third didn't show for some users" bug). Poll the ticket-gated program
  // state as a safety net, but ONLY once the data channel has gone quiet: a recent
  // data message means the fan is already current, so skipping avoids reverting a
  // just-received update that hasn't finished persisting on the director side.
  useEffect(() => {
    if (phase !== "webrtc") return;
    // The headless renderer (LSC-429) holds no session, so the ticket-gated backstop
    // read would 401 — it relies on the director data channel it receives as a live
    // room participant. A code-authed render backstop is a possible later refinement.
    if (renderCode) return;
    const id = window.setInterval(() => {
      if (!mountedRef.current) return;
      if (Date.now() - lastProgramMsgRef.current < PROGRAM_POLL_GRACE_MS) return;
      fetchWatchProgram(broadcastId)
        .then((p) => {
          if (mountedRef.current) applyProgram(p);
        })
        .catch(() => undefined);
    }, PROGRAM_POLL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, broadcastId]);

  // Pre-live go-live poll (LSC-574): until the show has gone live for this fan, poll
  // the program fast + ungated and latch as soon as the server reports `program.live`
  // (the Director's TAKE). This guarantees a fan connected before the TAKE leaves the
  // pre-start/pre-roll state within a couple seconds even if every data-channel message
  // is dropped — the Oct-9 Jam failure — without touching the grace-gated general
  // backstop (which protects mid-show overlay updates from reverting). Monotonic: it
  // only acts when live becomes true, and tears down the moment hasBeenLive latches.
  useEffect(() => {
    if (phase !== "webrtc" || renderCode || hasBeenLive) return;
    const id = window.setInterval(() => {
      if (!mountedRef.current) return;
      fetchWatchProgram(broadcastId)
        .then((p) => {
          if (mountedRef.current && p.live) applyProgram(p);
        })
        .catch(() => undefined);
    }, GOLIVE_POLL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, broadcastId, renderCode, hasBeenLive]);

  useEffect(() => {
    // Not a signal loss to recover: a deliberate video hold (ADR 0053/0055)
    // intentionally covers the feed, and standby/ended are voluntary — skip so we
    // never fight the hold→clear re-attach (ADR 0062) or mis-report a held source.
    if (phase !== "webrtc" || program.standby || program.ended || program.videoPaused) return;
    const slots = layoutSlots(program.layout);
    const needA = !!program.source && sourceKind === "none";
    const needB = slots.includes("b") && !!program.sourceB && sourceKindB === "none";
    const needC = slots.includes("c") && !!program.sourceC && sourceKindC === "none";
    const needD = slots.includes("d") && !!program.sourceD && sourceKindD === "none";
    if (!needA && !needB && !needC && !needD) {
      lostReportedRef.current = false; // recovered — re-arm the report
      return;
    }
    // Re-request the source on a bounded backoff. A 3-shot / 3s window was too
    // short under many-viewer load — a late/second subscription the SFU is slow to
    // deliver would close the window still "none" and, with no further RoomEvent to
    // re-arm this effect, sit on a permanent signal-loss slate only a refresh
    // cleared (ADR 0075). Stretch to 8 attempts across ~30s (each recovery cancels
    // the rest via the cleanup below), which the TrackSubscriptionFailed handler
    // above complements for the outright-failure case.
    const timers = [400, 1200, 3000, 6000, 10000, 15000, 22000, 30000].map((ms) =>
      window.setTimeout(() => attachSource(), ms),
    );
    // Field signal (ADR 0075 / observability DoD): if the source is STILL unresolved
    // after the whole retry window, record ONE playback_failed so the data shows how
    // often a Fan hits genuinely unrecoverable signal-loss (the second-publisher
    // race) — fired at the end so a slow-but-eventual recovery isn't a false
    // positive (the cleanup cancels it on recovery). Re-armed once the source
    // recovers.
    timers.push(
      window.setTimeout(() => {
        if (!mountedRef.current || lostReportedRef.current) return;
        lostReportedRef.current = true;
        const lost = [needA && "a", needB && "b", needC && "c", needD && "d"].filter(Boolean) as string[];
        const slot = lost.length > 1 ? lost.join("+") : lost[0] || "a";
        diag?.record("source:lost", slot); // on-program source unresolved after the full retry window
        track("playback_failed", {
          cavecastId: broadcastId,
          props: { error: "source-signal-lost", slot },
        });
      }, 33000),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, program.standby, program.ended, program.videoPaused, program.layout, program.source, program.sourceB, program.sourceC, program.sourceD, sourceKind, sourceKindB, sourceKindC, sourceKindD]);

  // Resolve the cave's pre/post-roll playlists once the cave is known. Best-effort
  // marketing content — a failure just falls back to the static slates.
  useEffect(() => {
    if (!caveId) return;
    let cancelled = false;
    fetchResolvedRoll(caveId)
      .then((pl) => {
        if (cancelled) return;
        setRollPre(pl.pre);
        setRollPost(pl.post);
        setRollHold(pl.hold ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setRollPre([]);
        setRollPost([]);
        setRollHold([]);
      });
    return () => {
      cancelled = true;
    };
  }, [caveId]);

  // Timed auto-resume (ADR 0055): while a hold carries a future resumeAt, arm a
  // one-shot timer that bumps holdTick when it elapses, so the visual hold clears
  // on its own (matching the server reaper) even with no further operator action.
  useEffect(() => {
    const ra = program.resumeAt;
    if (!ra) return;
    const ms = ra - Date.now();
    if (ms <= 0) return; // already elapsed — the derived check handles it
    const t = setTimeout(() => setHoldTick((k) => k + 1), ms);
    return () => clearTimeout(t);
  }, [program.resumeAt]);

  // The hold is active only until its timed auto-resume elapses (ADR 0055); with
  // no resumeAt it holds until an operator resumes. holdTick forces the recompute
  // when the timer fires.
  const holdActive = useMemo(
    () => !program.resumeAt || Date.now() < program.resumeAt,
    [program.resumeAt, holdTick],
  );

  // Apply the fan's own volume + mute (ADR 0064) AND the production-hold mute
  // (ADR 0053/0055) to every program media element (both audios in a multi-head
  // layout — the fan hears BOTH heads, ADR 0054 — and the video elements, which
  // carry the audio on the HLS path). The two mutes are INDEPENDENT concerns:
  // the element is muted if the fan muted it OR production muted it; the element
  // volume is always the fan's own. Applied via the element properties so a track
  // re-attach (attachSource) preserves the choice. The autoplay-policy "Enable
  // audio" gesture (ADR 0035) stays orthogonal — it resumes a blocked audio
  // context and never touches these element-level settings.
  // Apply the fan's OWN listening prefs to every program media element. Extracted
  // so the hold→resume re-attach can re-assert it (LSC-330): on iOS a track
  // re-attach can reset element.muted, dropping the fan's manual mute after a hold.
  const applyListenAudio = useCallback(() => {
    const productionMuted = !!program.audioMuted && holdActive;
    // A full-frame slate REPLACES the program source for the AUDIENCE (LSC-560): an ad /
    // intermission slate (overlay.kind "ad"), a standby "be right back", or the ended
    // slate. Mute the live source audio in those states so an off-focus guest/legend is
    // never heard talking under a slate. Scope is the audience path only — the talent
    // conversation plane (ADR 0218, talent surfaces) and the director monitor (ADR 0085)
    // are untouched, so backstage talk stays audible to talent off-program. An ad's OWN
    // audio (AdSurface) is unaffected. A lower-third / whiteboard-over-source is an
    // OVERLAY not a replacement (source stays the show → stays audible), and a
    // video-pause hold is left to the operator's explicit audioMuted choice.
    const slateActive = program.overlay?.kind === "ad" || !!program.standby || !!program.ended;
    const muted = userMuted || productionMuted || slateActive;
    // HLS delivery is a plain <video> with muxed audio (NOT a LiveKit track), so its audio
    // is controlled at the element and is unaffected by webAudioMix.
    if (phase === "hls") {
      const v = videoRef.current;
      if (v) {
        v.muted = muted;
        v.volume = volume;
      }
      return;
    }
    // WebRTC fan path: the Room runs with webAudioMix (LSC-598), so remote audio is mixed
    // through ONE Web Audio graph and the <audio> elements are kept muted by LiveKit —
    // element.muted/.volume no longer control output (and unmuting an element would
    // DOUBLE-play, per livekit-client). Apply the fan's volume/mute to the remote audio
    // TRACKS via setVolume, which drives the graph gain — and actually works on iOS, where
    // element.volume is inert (ADR 0076). Every active head (A/B/C/D, ADR 0212) stays
    // audible together at the fan's level.
    if (!renderCode) {
      const vol = muted ? 0 : volume;
      for (const t of [
        attachedAudioRef.current, attachedAudioRefB.current,
        attachedAudioRefC.current, attachedAudioRefD.current,
      ]) {
        t?.setVolume?.(vol);
      }
      return;
    }
    // Headless program-render egress (renderCode): webAudioMix is OFF (new Room(undefined)),
    // so audio plays per-element — headless Chrome sustains multiple elements and the egress
    // captures them. Keep the element path here only.
    for (const el of [
      audioRef.current, audioRefB.current, audioRefC.current, audioRefD.current,
      videoRef.current, videoRefB.current, videoRefC.current, videoRefD.current,
    ]) {
      if (!el) continue;
      el.muted = muted;
      el.volume = volume;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMuted, volume, program.audioMuted, holdActive, program.overlay?.kind, program.standby, program.ended, phase, renderCode]);
  useEffect(() => {
    applyListenAudio();
  }, [applyListenAudio, sourceKind, sourceKindB, sourceKindC, sourceKindD, program.layout, phase, fullscreen]);

  // Production-hold recovery (ADR 0062). When a hold LIFTS — audioMuted/videoPaused
  // flips back off, or a timed resumeAt elapses (the client timer / server reaper
  // clears the flag with NO data message on that path) — the on-program SFU track
  // has been unmuted server-side (ADR 0055), but a track muted mid-stream doesn't
  // reliably resume playback on its own. Force a re-attach of the on-program
  // source's track(s) (livekit's attach re-plays the element) so audio + video come
  // back with no need to rebuild the publisher. The prior-value guard fires this
  // ONLY on the hold→clear transition, so a healthy feed is never churned.
  const heldRef = useRef(false);
  useEffect(() => {
    const held = (!!program.audioMuted || !!program.videoPaused) && holdActive;
    if (heldRef.current && !held) {
      attachSource(true);
      // Re-assert the fan's own mute/volume — the re-attach can reset element.muted
      // on iOS, which would otherwise unmute a fan who muted before the hold (LSC-330).
      applyListenAudio();
      requestAnimationFrame(applyListenAudio); // and after the attach settles
    }
    heldRef.current = held;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.audioMuted, program.videoPaused, holdActive]);

  // Signal-lost grace (ADR 0062). Compute the raw single-layout "source assigned
  // but no live track" condition from state, then reveal the slate only after a
  // short grace — so a source drop during a deliberate End/standby teardown never
  // flashes "Signal lost"; an intentional terminal state (ended/standby) arriving
  // in the meantime clears the raw condition and cancels the timer. A genuine loss
  // still surfaces the slate after the grace. (Multi-head per-tile loss is
  // suppressed under the same terminal slates and stays instant.)
  useEffect(() => {
    const overlay = program.overlay;
    const multi = isMultiHead(program.layout);
    const paused =
      (phase === "webrtc" || phase === "hls") &&
      !!program.videoPaused &&
      holdActive &&
      !program.standby &&
      !program.ended &&
      overlay?.kind !== "ad";
    const rawLost =
      phase === "webrtc" &&
      !multi &&
      !!program.source &&
      !sourceLive &&
      hasBeenLive && // pre-live (never been live) shows the pre-start slate, not signal-lost (ADR 0101)
      !program.standby &&
      !program.ended &&
      !paused &&
      overlay?.kind !== "ad";
    if (!rawLost) {
      setShowSourceLost(false);
      return;
    }
    // The on-program source just dropped. Immediately re-sync the PERSISTED program
    // (ADR 0091 backstop) so an INTENTIONAL end/standby — which relays {ended}/
    // {standby} over the lossy data channel and persists it — resolves to its
    // terminal slate within one request, instead of the fan lingering on "Signal
    // lost"/black until the periodic poll fires (ADR 0104: intentional end must read
    // as intentional, not as a signal loss). A genuine transient drop just re-reads
    // the same live state and the grace timer still shows signal-lost below.
    fetchWatchProgram(broadcastId)
      .then((p) => {
        if (mountedRef.current) applyProgram(p);
      })
      .catch(() => undefined);
    const t = setTimeout(() => {
      if (mountedRef.current) setShowSourceLost(true);
    }, SIGNAL_LOST_GRACE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phase,
    broadcastId,
    program.source,
    program.layout,
    program.standby,
    program.ended,
    program.videoPaused,
    program.overlay,
    sourceLive,
    hasBeenLive,
    holdActive,
  ]);

  // Latch "has been live" on the Director's TAKE — `program.live` (LSC-268 / iOS
  // ADR 0014: "live begins at the director's TAKE", superseding the ADR 0101 video-
  // frame heuristic). The legend merely publishing (warming up) never sets it, and a
  // source that is only STAGED never sets it, so the pre-start slate persists until
  // the Director actually takes the show on air — no more "warming up" leak before
  // the show starts (LSC-180). Reset on end so the next go-live shows the waiting
  // slate again (the fresh-go-live program reset clears program.live too). A program
  // that never carries the field (old client / HLS path, which is excluded from the
  // pre-start gate anyway) simply never latches here.
  useEffect(() => {
    if (program.live) setHasBeenLive(true);
  }, [program.live]);
  useEffect(() => {
    if (program.ended) setHasBeenLive(false);
  }, [program.ended]);

  const leave = () => {
    onClose?.();
  };

  const enableAudio = async () => {
    diag?.record("audio:enable-tapped"); // the fan took the Enable-audio action (AC)
    // Resume the shared context SYNCHRONOUSLY, inside the tap (LSC-638) — iOS ignores a
    // resume() issued after an await. startAudio then also plays LiveKit's iOS dummy
    // element (media-playback audio session) and refreshes its own status.
    unlockAudio();
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.startAudio();
      if (mountedRef.current) setAudioBlocked(!room.canPlaybackAudio);
    } catch {
      if (mountedRef.current) setAudioBlocked(true);
    }
    // The shared context is the source of truth when present (see syncBlocked).
    const c = getSharedAudioContext();
    if (c && mountedRef.current) setAudioBlocked(c.state !== "running");
  };

  // "Report a problem" (LSC-598): post the session's diagnostic ring buffer + an opaque
  // reference without leaving Watch; show the returned reference so the fan can quote it to
  // Ops. Best-effort — a telemetry failure shows a soft "couldn't send" and never disrupts
  // the show. `symptom` is an optional coarse label the fan picks.
  const reportProblem = useCallback(
    (symptom: string) => {
      if (!diag || report.state === "sending") return;
      diag.record("fan:reported", symptom);
      setReport({ state: "sending" });
      void submitPlaybackReport({
        sessionRef: diag.sessionRef,
        broadcastId,
        delivery: phase === "hls" ? "hls" : phase === "webrtc" ? "webrtc" : "",
        symptom,
        events: diag.events(),
      }).then((ref) => {
        if (mountedRef.current) setReport({ state: "done", ref: ref ?? undefined });
      });
    },
    [diag, report.state, broadcastId, phase],
  );

  if (phase === "ticket-required") {
    return (
      <div style={surfaceStyle} aria-label="Watch live program">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ ...monoLabelStyle, color: "var(--text-hi)" }}>Watch</span>
          {onClose && (
            <div style={{ marginLeft: "auto" }}>
              <Button size="sm" variant="muted" aria-label="Leave" onClick={leave}>
                Leave
              </Button>
            </div>
          )}
        </div>
        <p role="alert" style={{ margin: 0, fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
          {gate === "signin"
            ? "Sign in to watch this program."
            : gate === "unavailable"
              ? gateMsg || "This broadcast isn't available right now."
              : "You need a ticket to watch."}
        </p>
        {/* The storefront CTA only for a genuine ticket/sign-in gate — never for an
            "unavailable" 403 (e.g. a private rehearsal), where it would misdirect a
            holder to buy a ticket they may already have (LSC-620). Leave (top) exits. */}
        {gate !== "unavailable" && (onGetTicket || onClose) && (
          <div>
            <Button
              variant="accent"
              aria-label={gate === "signin" ? "Sign in" : "Get a ticket"}
              onClick={() => (onGetTicket ?? onClose)?.()}
            >
              {gate === "signin" ? "Sign in" : "Get a ticket"}
            </Button>
          </div>
        )}
      </div>
    );
  }

  const overlay = program.overlay;
  // Ask-the-Legend is ON by default; the Director or Legend can turn it off for a
  // CaveCast (ADR 0077), which hides the Fan submit rail everywhere it appears.
  const askEnabled = !program.askDisabled;
  // Multi-head layout (ADR 0054): the "live" composition renders two heads (and,
  // for dual-board/dueling, telestration board region[s]) client-side. The
  // voluntary/deliberate slates below (ended > standby > ad > hold) still outrank
  // and replace the WHOLE composition exactly as for single; only the involuntary
  // signal-lost handling changes — it goes PER-TILE.
  const multiHead = isMultiHead(program.layout);
  // Production hold — video paused (ADR 0053). A DELIBERATE production state: the
  // resolved hold image (or a text "Paused" fallback) pops over the stage in
  // place of the on-program video. It OUTRANKS the involuntary signal-lost slate
  // (ADR 0042) and the audio-led stage (ADR 0043), but the voluntary lifecycle
  // slates (standby/ended) still outrank it, and an explicitly played ad still
  // shows (the hold applies to the program feed, not an ad break).
  const videoPaused =
    (phase === "webrtc" || phase === "hls") &&
    !!program.videoPaused &&
    holdActive &&
    !program.standby &&
    !program.ended &&
    overlay?.kind !== "ad";
  // Pre-start "Starting soon" waiting slate (ADR 0064/0101). Shows while the show
  // has NOT yet been live this cycle — PERSISTENT, not gated on `!program.source`,
  // so assigning a source before the first live frame no longer collapses the slate
  // to a black/signal-lost frame (Beta Test 8). The standby-pause and end slates
  // (ADR 0039/0025) own the screen when set; suppressed under a hold.
  const showStandby =
    phase !== "hls" && !hasBeenLive && !program.standby && !program.ended && !videoPaused;
  // Involuntary source loss (ADR 0042): a source IS on program but no live
  // participant currently matches it — distinct from standby/ended (voluntary).
  // An ad break intentionally replaces the feed, so it isn't "signal lost"; nor
  // is a deliberate video pause (ADR 0053), which outranks it. In single layout
  // this is a full-stage slate; in a multi-head layout each tile owns its own
  // signal-lost slate (below), so the full-stage one is suppressed. The slate is
  // GRACE-GATED (`showSourceLost`, ADR 0062) so a source drop during a deliberate
  // End/standby teardown never flashes it — `ended`/`standby` still strictly
  // outrank it here too.
  const sourceLost = showSourceLost && !program.standby && !program.ended;
  // Per-tile signal loss (ADR 0054/0042): a slot has a source assigned but no live
  // track. The OTHER tile keeps playing. Suppressed under the deliberate
  // ad/hold/standby/ended slates that already replace the whole composition.
  const tileLostSuppressed = program.standby || program.ended || videoPaused || overlay?.kind === "ad";
  const activeSlots = layoutSlots(program.layout);
  const slotALost = multiHead && phase === "webrtc" && !!program.source && sourceKind === "none" && !tileLostSuppressed;
  const slotBLost = multiHead && phase === "webrtc" && !!program.sourceB && sourceKindB === "none" && !tileLostSuppressed;
  const slotCLost = activeSlots.includes("c") && phase === "webrtc" && !!program.sourceC && sourceKindC === "none" && !tileLostSuppressed;
  const slotDLost = activeSlots.includes("d") && phase === "webrtc" && !!program.sourceD && sourceKindD === "none" && !tileLostSuppressed;
  // Audio-only program (ADR 0043): the on-program source is live but publishing
  // mic only. Show an intentional audio-led stage (legend hero + "Audio
  // broadcast") instead of a blank/black <video> — distinct from signal-lost
  // (the source IS live) and from standby/ended (voluntary). Overlays and an ad
  // break draw on top; it flips back to the video stage the moment a video track
  // appears (sourceKind → "video"). A deliberate video pause (ADR 0053) outranks
  // it — the hold image replaces the audio-led stage too.
  const audioOnly =
    phase === "webrtc" &&
    !multiHead &&
    sourceKind === "audio" &&
    !program.standby &&
    !program.ended &&
    !videoPaused;
  // Fade-to-black (ADR 0081/0082): the Director blacked out PROGRAM. Covers the
  // live composition + program overlays (and an involuntary signal-lost slate — a
  // deliberate black wins), but NOT the voluntary lifecycle slates: standby/ended
  // still communicate their state. The layer is always mounted (opacity-toggled)
  // so it fades both ways via a CSS transition.
  const blackoutOn =
    (phase === "webrtc" || phase === "hls") &&
    !!program.blackout &&
    !program.standby &&
    !program.ended;

  const liveBadge = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          background: "var(--alert-2)",
          borderRadius: "50%",
          boxShadow: "var(--glow-accent)",
        }}
      />
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-2xs)",
          letterSpacing: "var(--ls-caps)",
          color: "var(--alert-2)",
        }}
      >
        LIVE
      </span>
    </span>
  );

  // The video stage — the same <video> (one element at a time) sized inline
  // (16/9 card) or full-bleed (fills the shell). Overlays drawn on top. A
  // whiteboard on program composes with the legend feed per its layout (ADR 0022).
  const stage = (full: boolean) => {
    // The whiteboard overlay (ADR 0022/0045) composes over the feed via its pip/side/
    // full layout — for `single` AND `dual` (two-up) scenes (ADR 0116: the whiteboard
    // is treated as a compositable source, so a board armed in two-up now shows). The
    // `dual-board`/`dueling` layouts instead render the board as a dedicated region of
    // the composition (dualBoard / program.boards below), so the overlay is skipped
    // there to avoid a double board.
    const wb =
      overlay?.kind === "whiteboard" && !layoutHasBoard(program.layout)
        ? overlay
        : null;
    const lay = wb ? whiteboardLayout(wb.layout) : null;
    // dual-board's shared board is the ADR 0045 whiteboard overlay (board+strokes),
    // rendered read-only in the composition's board region.
    const dualBoard = overlay?.kind === "whiteboard" ? overlay : null;
    // Is the program-swap transition snapshot (ADR 0082/0098) currently showing?
    // It only fires on a live source swap, but a voluntary slate (standby/ended)
    // or a hold must never leave the frozen frame on top — guard defensively.
    const snapVisible = !!snap && !program.ended && !program.standby && !videoPaused && !showStandby;
    return (
      <div style={full ? { ...stageStyle, flex: 1, aspectRatio: "auto", minHeight: 0 } : stageStyle}>
        {/* Director mic → On Air (ADR 0247): the Director's public on-air audio plays
            here (hidden), independent of the program composition. Present on every watch
            surface — including the headless program-renderer — so live + recording hear it. */}
        <div ref={directorMicRef} aria-hidden style={{ display: "none" }} />
        {multiHead && program.layout ? (
          // Multi-head composition (ADR 0054), composited CLIENT-SIDE — two heads
          // (and board region[s]) laid out per the layout; BOTH heads' audio is
          // attached and playing. Overlays/slates below still draw over this.
          <div style={layoutContainerStyle(program.layout)} aria-label={`Program layout ${program.layout}`}>
            <div style={layoutTileStyle("a")}>
              <video ref={videoRef} autoPlay playsInline aria-label="Live program video" style={videoStyle} />
              {slotALost && <div style={tileLostStyle} aria-label="Slot A signal lost">Signal lost — reconnecting…</div>}
            </div>
            <div style={layoutTileStyle("b")}>
              <video ref={videoRefB} autoPlay playsInline aria-label="Live program video (slot B)" style={videoStyle} />
              {slotBLost && <div style={tileLostStyle} aria-label="Slot B signal lost">Signal lost — reconnecting…</div>}
            </div>
            {activeSlots.includes("c") && (
              <div style={layoutTileStyle("c")}>
                <video ref={videoRefC} autoPlay playsInline aria-label="Live program video (slot C)" style={videoStyle} />
                {slotCLost && <div style={tileLostStyle} aria-label="Slot C signal lost">Signal lost — reconnecting…</div>}
              </div>
            )}
            {activeSlots.includes("d") && (
              <div style={layoutTileStyle("d")}>
                <video ref={videoRefD} autoPlay playsInline aria-label="Live program video (slot D)" style={videoStyle} />
                {slotDLost && <div style={tileLostStyle} aria-label="Slot D signal lost">Signal lost — reconnecting…</div>}
              </div>
            )}
            {layoutHasSharedBoard(program.layout) && (
              <div style={layoutTileStyle("board")}>
                <WhiteboardCanvas
                  strokes={dualBoard?.strokes ?? []}
                  board={dualBoard?.board}
                  boardImageUrl={resolveBoardImageUrl(dualBoard?.board, boardSlates)}
                  ariaLabel="Shared whiteboard"
                  style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }}
                />
              </div>
            )}
            {program.layout === "dueling" && (
              <>
                <div style={layoutTileStyle("boardA")}>
                  <WhiteboardCanvas
                    strokes={program.boards?.a?.strokes ?? []}
                    board={program.boards?.a?.board}
                    boardImageUrl={resolveBoardImageUrl(program.boards?.a?.board, boardSlates)}
                    ariaLabel="Slot A board"
                    style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }}
                  />
                </div>
                <div style={layoutTileStyle("boardB")}>
                  <WhiteboardCanvas
                    strokes={program.boards?.b?.strokes ?? []}
                    board={program.boards?.b?.board}
                    boardImageUrl={resolveBoardImageUrl(program.boards?.b?.board, boardSlates)}
                    ariaLabel="Slot B board"
                    style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }}
                  />
                </div>
              </>
            )}
            <audio ref={audioRef} autoPlay aria-hidden />
            <audio ref={audioRefB} autoPlay aria-hidden />
            {activeSlots.includes("c") && <audio ref={audioRefC} autoPlay aria-hidden />}
            {activeSlots.includes("d") && <audio ref={audioRefD} autoPlay aria-hidden />}
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              aria-label="Live program video"
              style={lay ? { ...videoStyle, ...lay.video } : videoStyle}
            />
            <audio ref={audioRef} autoPlay aria-hidden />
          </>
        )}
        {/* Program-swap transition (ADR 0081/0082): the frozen OUTGOING frame,
            animating away (crossfade / wipe / slide) to reveal the freshly-attached
            new program underneath. Always mounted so the ref exists at capture time;
            invisible + non-interactive unless a transition is running. Sits above the
            video, below the controls/overlays. */}
        <canvas
          ref={snapshotCanvasRef}
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 2,
            pointerEvents: "none",
            // The WAAPI animation (LSC-176) owns the animated props (opacity /
            // transform / clip-path) while it runs, so React only manages presence:
            // the frozen frame is shown (opacity 1) the instant a transition starts
            // and hidden otherwise. visibility force-hides it under a voluntary slate
            // / hold (snapVisible), and opacity:0 when idle keeps any leftover geometry
            // from flashing before/after the animation.
            display: "block",
            visibility: snapVisible ? "visible" : "hidden",
            opacity: snapVisible ? 1 : 0,
          }}
        />
        {audioBlocked && (
          <div style={{ position: "absolute", top: 10, right: 10, zIndex: 3 }}>
            <Button size="sm" variant="accent" aria-label="Enable audio" onClick={() => void enableAudio()}>
              Enable audio
            </Button>
          </div>
        )}
        {/* The fan's own volume + mute (ADR 0064) — an obvious, always-available
            listening control over the stage, present across every watch layout
            (this stage is shared by the Fan View, compact watch, and full-screen)
            and the audio-led stage. Distinct from the autoplay-policy "Enable
            audio" gesture above and the production/hold mute. */}
        {(phase === "webrtc" || phase === "hls") && (
          <div style={{ position: "absolute", left: 10, top: 10, zIndex: 3 }}>
            <VolumeControl
              volume={volume}
              muted={userMuted}
              onVolumeChange={setVolumePersist}
              onMutedChange={setMutedPersist}
              hideSlider={IS_IOS}
            />
          </div>
        )}
        {/* Report a playback problem (LSC-598) — a lightweight affordance a fan uses
            WITHOUT leaving the show. It posts this session's diagnostic ring buffer +
            an opaque reference and shows the returned reference to quote to Ops. Only a
            real fan watch (not the headless renderer) on a live/HLS stage. */}
        {diag && (phase === "webrtc" || phase === "hls") && (
          <div style={{ position: "absolute", left: 10, bottom: 10, zIndex: 3, maxWidth: "min(78vw, 300px)" }}>
            {report.state === "done" ? (
              <div style={reportNoteStyle} role="status">
                {report.ref ? (
                  <>Thanks — reported. Reference <strong>{report.ref}</strong>. Share it with support if it keeps happening.</>
                ) : (
                  <>Thanks — we noted it. (Couldn't send the full report just now.)</>
                )}
              </div>
            ) : reportOpen ? (
              <div style={reportNoteStyle}>
                <div style={{ marginBottom: 6, color: "var(--text-dim)" }}>What's wrong?</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {([
                    ["no_audio", "No sound"],
                    ["frozen_video", "Frozen / black video"],
                    ["missing_video", "Missing a guest"],
                    ["other", "Something else"],
                  ] as const).map(([val, label]) => (
                    <Button
                      key={val}
                      size="sm"
                      variant="muted"
                      aria-label={`Report: ${label}`}
                      disabled={report.state === "sending"}
                      onClick={() => {
                        reportProblem(val);
                        setReportOpen(false);
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <Button size="sm" variant="muted" aria-label="Report a playback problem" onClick={() => setReportOpen(true)}>
                ⚑ Report a problem
              </Button>
            )}
          </div>
        )}
        {/* Audio-led stage (ADR 0043) — a base layer under the overlays; the mic
            audio is already attached to the <audio> above and playing (the
            Enable-audio gesture covers autoplay-blocked browsers). */}
        {audioOnly && (
          <div style={audioStageStyle} aria-label="Audio broadcast">
            {legend && <div aria-hidden style={audioInitialsStyle}>{legend.initials}</div>}
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "clamp(20px, 3.2vw, 32px)",
                fontWeight: 700,
                color: "var(--text-hi)",
                textTransform: "uppercase",
                letterSpacing: "0.01em",
              }}
            >
              {legend?.name ?? "Live audio"}
            </div>
            {(legend?.position || legend?.team) && (
              <div style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
                {[legend?.position, legend?.team].filter(Boolean).join(" · ")}
              </div>
            )}
            <div className="audio-eq" aria-hidden>
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className="audio-eq__bar" style={{ animationDelay: `${i * 0.12}s` }} />
              ))}
            </div>
            <div style={{ ...monoLabelStyle, color: "var(--gold)" }}>Audio broadcast</div>
          </div>
        )}
        {/* Production hold — video paused (ADR 0055): the resolved rotating HOLD
            playlist loops over the stage in place of the on-program video (a text
            "Paused" slate when the playlist is empty). A base layer, so lower-third/
            whiteboard overlays and the Ask-the-Legend rail still compose over it; it
            clears the instant the flag flips off or the timed auto-resume elapses. */}
        {videoPaused &&
          (rollHold.length > 0 ? (
            <RollPlayer items={rollHold} ariaLabel="Hold" />
          ) : (
            <div style={{ ...standbyStyle, background: "radial-gradient(120% 120% at 50% 0%, #23242a 0%, #0c0c0f 70%)", flexDirection: "column", gap: 8 }} aria-label="Program paused">
              <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(20px, 3.4vw, 34px)", fontWeight: 700, color: "var(--text-hi)" }}>
                Paused
              </div>
              <div style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
                We'll be right back.
              </div>
            </div>
          ))}
        {/* Pre-live/waiting (ADR 0052/0064): when a pre-roll playlist is configured
            it loops (0052 wins for pre-live content); otherwise the designed,
            branded PRE-START slate is the default — a distinct "hasn't started yet"
            experience (not "the Cave"), reusing the legend/cave context. Distinct
            from standby (a mid-show pause) and ended (terminal).
            A staged whiteboard overlay still previews off-air for the single/dual
            (non-board) layouts (ADR 0063; `wb` is set only there). But a board
            LAYOUT — the shared dual-board/board-tri/board-quad OR dueling's per-slot
            boards (`program.boards`) — stays BEHIND the slate until the Director's
            TAKE: a fan told the show hasn't started should not be looking at its
            board composition (LSC-522, ratified — the slate wins until TAKE for all
            board layouts; this makes the shared-board case match dueling, which was
            already correct because its boards aren't an overlay). */}
        {showStandby && overlay?.kind !== "ad" && !wb &&
          (rollPre.length > 0 ? (
            <RollPlayer items={rollPre} ariaLabel="Pre-roll" />
          ) : (
            <div style={preStartStyle} aria-label="Starting soon">
              {legend && (
                <div aria-hidden style={audioInitialsStyle}>
                  {legend.initials}
                </div>
              )}
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "clamp(22px, 3.6vw, 38px)",
                  fontWeight: 700,
                  color: "var(--text-hi)",
                  textTransform: "uppercase",
                  letterSpacing: "0.01em",
                }}
              >
                {legend?.name ?? "The CaveCast"}
              </div>
              <div style={{ ...monoLabelStyle, color: "var(--gold)" }}>Starting soon</div>
              <div style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)", maxWidth: 420 }}>
                The CaveCast hasn't started yet. Hang tight — we'll be live the moment{" "}
                {legend?.name?.split(" ")[0] ?? "the legend"} goes on.
              </div>
            </div>
          ))}
        {/* The whiteboard, positioned per the layout (full / pip / side). */}
        {wb && lay && (
          <div style={lay.board}>
            <WhiteboardCanvas strokes={wb.strokes ?? []} board={wb.board} boardImageUrl={resolveBoardImageUrl(wb.board, boardSlates)} ariaLabel="Whiteboard on program" style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }} />
            {wb.text && <div style={lowerThirdStyle}>{wb.text}</div>}
          </div>
        )}
        {/* An ad break replaces the feed full-frame (video or sponsor slate). */}
        {overlay?.kind === "ad" && overlay.adId && <AdSurface overlay={overlay} />}
        {/* Lower third — a name/title band over the feed (ADR 0025). The Director's
            accent colour + left/centre position ride the opaque overlay (ADR 0088);
            honour them here, defaulting to the Cave gold, left-aligned. */}
        {overlay?.kind === "lower-third" && (overlay.title || overlay.subtitle) && (
          <div style={{ ...lowerThirdStyle, display: "flex", flexDirection: "column", gap: 3, alignItems: overlay.position === "center" ? "center" : "flex-start", textAlign: overlay.position === "center" ? "center" : "left" }}>
            <div style={{ width: 34, height: 3, borderRadius: 2, background: overlay.accent || "var(--gold)" }} />
            <div>
              <strong>{overlay.title}</strong>
              {overlay.subtitle ? (
                <span style={{ opacity: 0.85, fontWeight: 400 }}> · {overlay.subtitle}</span>
              ) : null}
            </div>
          </div>
        )}
        {/* The broadcast is paused in a recoverable standby (ADR 0039) — a
            distinct slate (not the terminal end slate); the show may resume. */}
        {program.standby && !program.ended && (
          <div style={{ ...standbyStyle, background: "radial-gradient(120% 120% at 50% 0%, #23242a 0%, #0c0c0f 70%)", flexDirection: "column", gap: 8 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(22px, 3.6vw, 38px)", fontWeight: 700, color: "var(--text-hi)" }}>
              Standby
            </div>
            <div style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
              The broadcast is paused. Hang tight — we'll be right back.
            </div>
          </div>
        )}
        {/* Involuntary source loss (ADR 0042) — the publisher on program dropped;
            show an intentional "reconnecting" slate, not a frozen/blank frame. It
            clears automatically when the source's track returns. */}
        {sourceLost && (
          <div style={{ ...standbyStyle, background: "radial-gradient(120% 120% at 50% 0%, #241f1a 0%, #0c0c0f 70%)", flexDirection: "column", gap: 8 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(20px, 3.4vw, 34px)", fontWeight: 700, color: "var(--text-hi)" }}>
              Signal lost — reconnecting…
            </div>
            <div style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
              We lost the feed for a moment. Hang tight — it'll be back as soon as it reconnects.
            </div>
          </div>
        )}
        {/* Fade to black (ADR 0081/0082) — a deliberate program blackout. Always
            mounted so it fades both ways via the CSS transition; it covers the live
            composition, overlays, and an involuntary signal-lost slate, but the
            voluntary standby/ended slates (below / guarded out) still show. */}
        <div
          role="status"
          aria-hidden={!blackoutOn}
          aria-label={blackoutOn ? "Program faded to black" : undefined}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 4,
            background: "#000",
            opacity: blackoutOn ? 1 : 0,
            pointerEvents: blackoutOn ? "auto" : "none",
            transition: "opacity 260ms ease",
          }}
        />
        {/* The broadcast has ended (ADR 0025) — an INTENTIONAL completion slate
            (distinct from the involuntary signal-lost one). Precedence (ADR 0104):
            the operator's custom "Broadcast Ended" board (program.endedBoard) if set,
            else the resolved post-roll (thanks/sponsor reel, ADR 0052), else the
            default "That's a wrap" card. */}
        {program.ended &&
          (program.endedBoard ? (
            <div style={{ ...standbyStyle, padding: 0, background: "#000" }} aria-label="Broadcast ended">
              <img
                src={program.endedBoard}
                alt="Broadcast ended"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
          ) : rollPost.length > 0 ? (
            <RollPlayer items={rollPost} ariaLabel="Post-roll" />
          ) : (
            <div style={{ ...standbyStyle, background: "radial-gradient(120% 120% at 50% 0%, #1f2024 0%, #0a0a0c 70%)", flexDirection: "column", gap: 8 }} aria-label="Broadcast ended">
              <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(24px, 4vw, 42px)", fontWeight: 700, color: "var(--text-hi)" }}>
                That's a wrap
              </div>
              <div style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
                This broadcast has ended. Thanks for watching.
              </div>
            </div>
          ))}
      </div>
    );
  };

  // Headless program-renderer (LSC-429): render ONLY the produced program stage,
  // full-bleed, with no fan chrome — this is exactly what the LiveKit composite egress
  // captures, so HLS/VOD show the produced program (source/layout/overlays/chroma/
  // transitions/slates), not a raw grid. Reuses stage() verbatim → zero drift from RTC.
  if (renderCode) {
    return (
      <div
        aria-label="Program render"
        // MUST be a flex column with a definite height: stage(true)'s root sizes its
        // height via `flex: 1` (its `full` branch drops the 16:9 aspectRatio), which is
        // INERT without a flex parent. A non-flex wrapper let the stage collapse to its
        // in-flow <video>'s height, so absolute slates/boards/roll sized to that shallow
        // box — a slate rendered as a small tile on a black field, and a whiteboard (which
        // makes the <video> absolute → no in-flow child → height 0) rendered fully black.
        // The fan FullscreenShell is already a flex column, which is why fans were fine.
        //
        // Height is `calc(100vh / var(--ui-zoom))` — NOT raw 100vh — and there is no
        // `width: 100vw` (inset:0 computes the width). The whole app runs under
        // `body { zoom: var(--ui-zoom) }` = 1.25 (framework base CSS), and CSS `zoom`
        // scales every painted length, so a raw 100vw×100vh box paints at 125% of the
        // egress capture viewport — the composition overflowed ~25% off the RIGHT and
        // BOTTOM (slate text off-edge, Legend inset clipped) and the bottom-anchored
        // lower-third was pushed off-frame entirely (LSC-431). (#475 fixed the earlier
        // *collapse* by making this a flex column but left the zoom overflow.) This is the
        // framework's documented vh rule and mirrors FullscreenShell's wrapper (the SAME
        // stage renders correctly for fans), so the capture == the fan view — and keeping
        // the 1.25 zoom preserves fidelity to exactly what fans saw.
        style={{ position: "fixed", inset: 0, height: "calc(100vh / var(--ui-zoom, 1))", background: "#000", overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        {stage(true)}
      </div>
    );
  }

  if (fullscreen) {
    return (
      <FullscreenShell
        title="Live program"
        status={liveBadge}
        onExit={() => setFullscreen(false)}
        // In portrait, stack the Program over the Ask rail so direct-portrait fullscreen
        // doesn't crush the video into a strip beside a 320px rail (LSC-172). Fan-only;
        // operator consoles keep the row layout.
        stackRailInPortrait
        rail={askEnabled && !theater ? <ChatRail messages={chat.messages} onSend={chat.send} variant="ask" title="Ask the Legend" /> : undefined}
      >
        {conn !== "live" && (
          <div style={{ marginBottom: 8 }}>
            <ConnectionBanner state={conn} onRejoin={rejoin} />
          </div>
        )}
        {stage(true)}
      </FullscreenShell>
    );
  }

  const header = (
    // flex-wrap + rowGap so the Watch controls (Full screen / Leave) wrap onto a
    // second line instead of overflowing a narrow phone viewport (LSC-172) — the
    // content-width buttons can't shrink, so without wrap the row pushes past the
    // screen and body's overflow-x:clip hides them off-screen (unreachable).
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", rowGap: 8 }}>
      <span style={{ ...monoLabelStyle, color: "var(--text-hi)" }}>Watch</span>
      {liveBadge}
      <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
        <Button
          size="sm"
          variant={theater ? "gold" : "muted"}
          aria-label="Theater mode"
          aria-pressed={theater}
          onClick={() => setTheaterPersist(!theater)}
        >
          {theater ? "▭ Show panels" : "▭ Theater"}
        </Button>
        <Button size="sm" variant="muted" aria-label="Full screen" onClick={() => setFullscreen(true)}>
          ⛶ Full screen
        </Button>
        {onClose && (
          <Button size="sm" variant="muted" aria-label="Leave" onClick={leave}>
            Leave
          </Button>
        )}
      </div>
    </div>
  );
  const connectionBanner = <ConnectionBanner state={conn} onRejoin={rejoin} />;
  const statusLine = (
    <>
      {connectionBanner}
      {phase === "loading" && (
        <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>Connecting…</p>
      )}
      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}
    </>
  );

  // Fan View (ADR 0020): a two-column watch — stage + whiteboard + athlete
  // live-stats, with a rail of sponsors + fan chat — when the cave's legend is
  // known. Falls back to the compact watch surface otherwise.
  const profile = legend?.profile;
  if (profile) {
    const sponsors = profile.sponsors;
    // Mobile-first grid (LSC-172): the Fan View column count lives in CSS (.fan-view),
    // single-column by DEFAULT and widened to two only at min-width:641px — so the phone
    // layout is query-independent and can't be left as a squeezed 2-col strip when the old
    // ≤640 `!important` override failed to fire under body{zoom} on iOS Safari. Theater
    // stays single-column via fan-view--theater.
    return (
      <section style={{ display: "grid", gap: 14 }} className={theater ? "fan-view fan-view--theater" : "fan-view"} aria-label="Fan view">
        <div style={{ display: "grid", gap: 12, minWidth: 0 }}>
          {header}
          {stage(false)}
          {statusLine}
          {!theater && (<>
          <Panel tier="quiet" title="Whiteboard from the legend">
            {program.overlay?.kind === "whiteboard" && (program.overlay.strokes?.length ?? 0) > 0 ? (
              <div style={{ display: "grid", gap: 8 }}>
                <WhiteboardCanvas strokes={program.overlay.strokes ?? []} board={program.overlay.board} boardImageUrl={resolveBoardImageUrl(program.overlay.board, boardSlates)} ariaLabel="Whiteboard from the legend" />
                {program.overlay.text && (
                  <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-hi)" }}>{program.overlay.text}</p>
                )}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-dim)", textAlign: "center", padding: "10px 0" }}>
                {legend?.name?.split(" ")[0] ?? "The legend"} hasn't shared a play yet…
              </p>
            )}
          </Panel>
          {profile.stats.length > 0 && (
            <Panel tier="base" title={`${legend?.name ?? "Athlete"} — Live Stats`}>
              <StatGrid items={profile.stats.map((s) => ({ value: s.val, label: s.label }))} columns={Math.min(6, profile.stats.length)} />
            </Panel>
          )}
          </>)}
        </div>

        {!theater && (
        <div style={{ display: "grid", gap: 12, alignContent: "start", minWidth: 0 }}>
          {sponsors && sponsors.presentedBy.length > 0 && (
            <Panel tier="base" title="Presented by">
              <div style={{ display: "grid", gap: 10 }}>
                {sponsors.presentedBy.map((s) => (
                  <div key={s.brand} style={{ padding: "10px 12px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-faint)", background: "var(--surface-quiet)", textAlign: "center" }}>
                    <div style={{ ...monoLabelStyle, color: "var(--text-dim)" }}>{s.tier}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--fs-lg)", color: "var(--info-hi)", margin: "3px 0 2px" }}>{s.brand}</div>
                    <div style={{ fontSize: "var(--fs-2xs)", color: "var(--text-muted)" }}>{s.offer}</div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
          {sponsors && sponsors.pills.length > 0 && (
            <Panel tier="quiet" title="Cave sponsors">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {sponsors.pills.map((name) => (
                  <span key={name} style={{ fontSize: "var(--fs-2xs)", color: "var(--text-2)", border: "1px solid var(--hairline)", borderRadius: "var(--radius-pill)", padding: "3px 11px", background: "var(--surface-2)" }}>
                    {name}
                  </span>
                ))}
              </div>
            </Panel>
          )}
          {askEnabled && (
            <div style={{ height: 360, display: "flex" }}>
              <ChatRail messages={chat.messages} onSend={chat.send} variant="ask" title="Ask the Legend" />
            </div>
          )}
        </div>
        )}
      </section>
    );
  }

  return (
    <div style={surfaceStyle} aria-label="Watch live program">
      {header}
      <div className={theater ? "consumer-watch-layout consumer-watch-layout--theater" : "consumer-watch-layout"}>
        {stage(false)}
        {askEnabled && !theater && (
          <aside className="consumer-watch-chat" aria-label="Ask the Legend panel">
            <ChatRail messages={chat.messages} onSend={chat.send} variant="ask" title="Ask the Legend" />
          </aside>
        )}
      </div>
      {statusLine}
    </div>
  );
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
